import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import type { MappingData } from "../../../../api/src/lib/dataMapping";
import type { ContactsController } from "../../../../api/src/contacts/contacts.controller";
import type { Contact } from "../../../../api/src/contacts/lib";
import type { UnpackResponse } from "../../../../api/src/lib";
import type { TargetMappingData } from "../../../../api/src/lib/dataMapping";
import type { ProfileController } from "../../../../api/src/profile/profile.controller";
import type { SlantLinkHeld } from "../../../../api/src/slant/slant-contact-link";
import Content from "../../components/Content";
import DataMappingModal from "../../components/DataMapping";
import { useErrorMessage } from "../../components/FormError";
import SubmitButton from "../../components/SubmitButton";
import { SlantLinkHeldAlert, slantLinksHeld } from "./SlantLinkHeld";
import {
  refusalSentence,
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../../lib/api";

type LinkData = Record<string, Record<string, string | number>>;

// The text in place of the two lists while no CRM is connected (C2-9,
// APP-2116, doc-contact-crm-mapping-page-01a1882bca358f94, R9). The text of
// the household page (Connect CRM R8), with "contacts" in place of
// "households".
const NO_CRM_CONNECTED =
  "No CRM is connected. Connect Wealthbox, Redtail or Slant to link contacts to CRM records.";

/**
 * The suggestions as `DataMappingModal` reads them: a map from contact ID to
 * the IDs of the CRM records suggested for it. The route answered that map
 * until APP-1653; it now answers one `{ contactId, recordId }` for each pair
 * (`ContactMappingSuggestion`, `api/src/contacts/contacts.controller.ts`).
 */
export function suggestedByContactId(
  suggested: readonly { contactId: number; recordId: number | string }[],
): Record<number, (number | string)[]> {
  return suggested.reduce<Record<number, (number | string)[]>>(
    (result, { contactId, recordId }) => ({
      ...result,
      [contactId]: [...(result[contactId] ?? []), recordId],
    }),
    {},
  );
}

// The Auto-Match suggestions of a CRM are read from this URL plus the CRM
// name. `useAuthenticatedFetch` uses the URL as the query key.
const SUGGESTIONS_URL = "/v1/contacts/data/mappings/suggestions/";

const ContactMapping = () => {
  const {
    isPending: isPendingProfile,
    isSuccess: isSuccessProfile,
    isError: isErrorProfile,
    data: dataProfile,
  } = useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
    "/profile",
  );
  const user = dataProfile?.data;

  const providerOptions = [
    ...(!user?.connections.wealthbox ? [] : ["wealthbox"]),
    ...(!user?.connections.redtail ? [] : ["redtail"]),
    ...(!user?.connections.slant ? [] : ["slant"]),
  ];
  // With no list of CRMs the modal shows the first CRM of the read, the
  // Wealthbox records (C2-9), so the page shows a text in its place (R9).
  const noCrmConnected = isSuccessProfile && providerOptions.length === 0;

  const { errorMessage, setError, resetError } = useErrorMessage();
  // A save that the api refused because a different contact holds a Slant
  // record of it (APP-1706, doc-held-slant-contact-links-01a11cd9686f430c,
  // R4, R5).
  const [held, setHeld] = useState<SlantLinkHeld[] | null>(null);

  const {
    isPending: isPendingMapping,
    isFetching: isFetchingMapping,
    isError: isErrorMapping,
    data: dataMapping,
  } = useAuthenticatedFetch<
    UnpackResponse<ContactsController["getContactMappings"]>
  >("/v1/contacts/data/mappings");

  const [service, setService] = useState(providerOptions[0] ?? "");

  // `GET /contacts/data/mappings/suggestions/:service` is served at `/v1`
  // only (APP-1653).
  const {
    isFetching: isFetchingSuggestions,
    data: dataSuggestions,
    refetch: fetchSuggestions,
  } = useAuthenticatedFetch<
    UnpackResponse<ContactsController["getContactMappingSuggestions"]>
  >(`${SUGGESTIONS_URL}${service}`, undefined, {
    enabled: false,
  });

  // A failed suggestions read keeps the rows and shows its text in the page
  // alert, as the household page does (C2-3, APP-2111,
  // doc-contact-crm-mapping-page-01a1882bca358f94, R29, R30). The alert goes
  // before the read is sent (R42).
  const onAutoSuggest = useCallback(async () => {
    resetError();

    const { isError, error } = await fetchSuggestions();
    if (isError) {
      const message = "Failed to auto-match contacts";
      setError(message);
      console.error(message, error);
    }
  }, [fetchSuggestions, setError, resetError]);

  const mapContacts = useAuthenticatedMutationAsync<
    UnpackResponse<ContactsController["mapContacts"]>
  >("/v1/contacts/data/mappings", async (data: LinkData) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const queryClient = useQueryClient();

  const { mutateAsync: onMap, isPending: isPendingOnMap } = useMutation({
    mutationFn: async (data: LinkData) => {
      resetError();
      setHeld(null);

      try {
        const mappedContacts = await mapContacts(data);
        // Clear the Auto-Match suggestions of every CRM after a successful
        // save, as the household page does (APP-1686). The modal adds each
        // suggestion to its row again when the mapping data changes, so a
        // saved suggested record would show two times (C2-2, APP-2110,
        // doc-contact-crm-mapping-page-01a1882bca358f94, R20). A failed save
        // does not reach this line and keeps the suggestions (R24). The query
        // is not enabled, so the reset does not fetch; a click on Auto-Match
        // does.
        await queryClient.resetQueries({
          predicate: ({ queryKey: [key] }) =>
            typeof key === "string" && key.startsWith(SUGGESTIONS_URL),
        });
        queryClient.setQueryData(["/v1/contacts/data/mappings"], {
          data: mappedContacts.data,
        });
      } catch (err) {
        const message = "Failed to connect contacts";
        console.error(message, err);
        const heldNow = slantLinksHeld(err);
        if (heldNow !== null) {
          setHeld(heldNow);
          return;
        }
        // A refusal shows the sentence of the api, as the Connect CRM page
        // does (C2-4, APP-2112, doc-contact-crm-mapping-page-01a1882bca358f94,
        // R25). Any other failure shows the fixed text.
        setError(refusalSentence(err) ?? message);
      }
    },
  });

  const handleLinkContacts = useCallback(
    async (newAssignedData: TargetMappingData<Contact>[]) => {
      const mappings = newAssignedData.reduce(
        (result, mappedContact) => ({
          ...result,
          ...Object.entries(mappedContact.assigned).reduce(
            (assignedResult, [provider, records]) => ({
              ...assignedResult,
              ...(records.length <= 0
                ? {}
                : {
                    [provider]: {
                      ...result[provider],
                      ...assignedResult[provider],
                      [mappedContact.id]: records[0].id,
                    },
                  }),
            }),
            {} as LinkData,
          ),
        }),
        {} as LinkData,
      );
      // A CRM whose last link the save removes is dropped by the reducer above,
      // so its key never reaches the API and the link stays (C2-1, APP-2109).
      // Send its key with an empty object: for each CRM in the body, the API
      // unlinks each contact the body leaves out, as on the household page
      // (doc-contact-crm-mapping-page-01a1882bca358f94, R18).
      for (const contact of dataMapping?.data.target ?? []) {
        for (const [provider, records] of Object.entries(contact.assigned)) {
          if (records.length > 0 && mappings[provider] === undefined) {
            mappings[provider] = {};
          }
        }
      }
      await onMap(mappings);
    },
    [onMap, dataMapping],
  );

  const mappingData = useMemo(
    () => ({
      ...(dataMapping?.data ?? {
        target: [],
        source: {},
      }),
      suggested: suggestedByContactId(dataSuggestions?.data.suggested ?? []),
    }),
    [dataMapping, dataSuggestions],
  );

  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // A change to a different CRM sets "Filter by Tag" to "All Tags" (C2-8,
  // APP-2115, doc-contact-crm-mapping-page-01a1882bca358f94, R40), so the next
  // create sends no tag of the earlier CRM. `DataMappingModal` reports the
  // shown CRM on the first render too: a change from no CRM keeps the tag.
  const [tagsService, setTagsService] = useState(service);
  if (tagsService !== service) {
    setTagsService(service);
    if (tagsService !== "") setSelectedTags([]);
  }

  const availableTags = useMemo(() => {
    const tags = new Set<string>();
    for (const item of mappingData.source[service] ?? []) {
      for (const tag of (item as MappingData).tags ?? []) {
        tags.add(typeof tag === "string" ? tag : tag.name);
      }
    }
    return Array.from(tags).sort();
  }, [mappingData.source, service]);

  const [isCreating, setIsCreating] = useState(false);

  const createNewContacts = useAuthenticatedMutationAsync<
    UnpackResponse<ContactsController["createContactsFromUnmapped"]>
  >(`/v1/contacts/data/mappings/${service}`, async () => ({
    method: "POST",
    body: JSON.stringify({
      tags: selectedTags.length > 0 ? selectedTags : undefined,
    }),
  }));

  const onCreateNewContacts = useCallback(async () => {
    resetError();
    setIsCreating(true);

    try {
      // Up to 100 creates while the last response has `remaining` and
      // `created` above 0: a create that creates no record stops them (C2-5,
      // APP-2113, R33). Each record of `notCreated` is named once by id, in
      // the order of the responses, when the creates end (C2-6, APP-1809,
      // R45); a failed create shows its own text instead (R39)
      // (doc-contact-crm-mapping-page-01a1882bca358f94).
      const MAX_BATCHES = 100;
      const notCreated = new Map<string, string>();
      let remaining = 1;
      let created = 1;
      for (let i = 0; i < MAX_BATCHES && remaining > 0 && created > 0; i++) {
        const result = await createNewContacts();
        remaining = result.data.remaining;
        created = result.data.created;
        queryClient.setQueryData(["/v1/contacts/data/mappings"], {
          data: result.data.mappings,
        });
        for (const record of result.data.notCreated ?? []) {
          const key = String(record.id);
          if (!notCreated.has(key)) notCreated.set(key, record.name);
        }
      }
      if (notCreated.size > 0) {
        setError(
          `Failed to create contacts for: ${[...notCreated.values()].join(", ")}.`,
        );
      }
    } catch (err) {
      const message = "Failed to create contacts";
      setError(message);
      console.error(message, err);
    } finally {
      setIsCreating(false);
    }
  }, [createNewContacts, setError, resetError, queryClient]);

  const createContactsButton = (
    <SubmitButton
      label="Create from Unlinked"
      onClick={onCreateNewContacts}
      isSubmitting={isCreating}
      // The caller `disabled` replaces the one `SubmitButton` sets from
      // `isSubmitting`, so `isCreating` stays in it (R36, R38, R46, R47,
      // APP-2114, doc-contact-crm-mapping-page-01a1882bca358f94).
      disabled={
        isCreating ||
        isPendingMapping ||
        isFetchingMapping ||
        isPendingProfile ||
        isErrorMapping ||
        isErrorProfile ||
        isPendingOnMap
      }
    />
  );

  const slantNameOf = useCallback(
    (slantContactId: string) =>
      (dataMapping?.data.source.slant ?? []).find(
        (record) => String(record.id) === slantContactId,
      )?.name,
    [dataMapping],
  );

  if (noCrmConnected) {
    return (
      <>
        <h1>Contact Data Connections</h1>
        <Content>
          <Alert variant="info" className="mb-0">
            {NO_CRM_CONNECTED}
          </Alert>
        </Content>
      </>
    );
  }

  return (
    <>
      {errorMessage}
      {held !== null && (
        <SlantLinkHeldAlert held={held} slantNameOf={slantNameOf} />
      )}
      <DataMappingModal
        linkHeader="/clients/contacts/"
        title="Contact Data Connections"
        targetTitle="Contact"
        providerOptions={providerOptions}
        sourceTitle="Unlinked"
        isLoading={isPendingMapping || isPendingProfile}
        isError={isErrorMapping || isErrorProfile}
        mappingData={mappingData}
        isSubmitting={isPendingOnMap}
        handleSubmit={handleLinkContacts}
        onAutoSuggest={onAutoSuggest}
        onSetService={setService}
        isLoadingSuggestions={isFetchingSuggestions}
        oneToMany={false}
        additionalButtons={createContactsButton}
        showNewRows={true}
        selectedTags={selectedTags}
        onTagFilterChange={setSelectedTags}
        availableTags={availableTags}
      />
    </>
  );
};

export default ContactMapping;
